import React, { useEffect, useState } from "react";
import { api, ApiError } from "../../api";
import { useToast } from "../../ToastContext";
import { RentReceipt } from "../shared/RentReceipt";
import { ChargeLines } from "../shared/ChargeLines";
import { useLanguage } from "../../i18n";
import { fmtMonth } from "../../format";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function currentFinancialYear(): string {
  const d = new Date();
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
}

function lastFinancialYears(n: number): string[] {
  const [startYear] = currentFinancialYear().split("-").map(Number);
  return Array.from({ length: n }, (_, i) => {
    const y = startYear - i;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  });
}

function CertificateView({ tenancyId, onBack }: { tenancyId: string; onBack: () => void }) {
  const { t } = useLanguage();
  const [year, setYear] = useState(currentFinancialYear());
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  function load(fy: string) {
    setLoading(true);
    api
      .get(`/api/ledger/tenancy/${tenancyId}/certificate?year=${fy}`)
      .then(setData)
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }} className="no-print">
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("tenantPayments.certificateTitle")}</div>
      </div>

      <div className="chip-row no-print" style={{ marginBottom: 14 }}>
        {lastFinancialYears(3).map((fy) => (
          <div
            key={fy}
            className={`chip${year === fy ? " selected" : ""}`}
            onClick={() => {
              setYear(fy);
              load(fy);
            }}
          >
            {fy}
          </div>
        ))}
      </div>

      {loading || !data ? (
        <div className="spinner" />
      ) : data.entries.length === 0 ? (
        <div className="empty-state">{t("tenantPayments.certificateEmpty", { year })}</div>
      ) : (
        <div className="card">
          <div style={{ fontSize: 12, color: "var(--ink-faint)", marginBottom: 10 }}>{t("tenantPayments.certificateDisclaimer")}</div>
          <div className="card-row" style={{ padding: "6px 0" }}>
            <span className="sub">{t("tenantPayments.certificateTenant")}</span>
            <b>{data.tenantName}</b>
          </div>
          <div className="card-row" style={{ padding: "6px 0" }}>
            <span className="sub">{t("tenantPayments.certificateOwner")}</span>
            <b>{data.ownerName || "—"}</b>
          </div>
          <div className="card-row" style={{ padding: "6px 0" }}>
            <span className="sub">{t("tenantPayments.certificateProperty")}</span>
            <b>{data.propertyAddress || data.propertyLabel}</b>
          </div>
          <div className="card-row" style={{ padding: "6px 0" }}>
            <span className="sub">{t("tenantPayments.certificateYear")}</span>
            <b>{data.financialYear}</b>
          </div>
          <div style={{ borderTop: "1px solid var(--line)", margin: "10px 0" }} />
          {data.entries.map((e: any) => (
            <div className="card-row" key={e.month} style={{ padding: "4px 0" }}>
              <span className="sub">{fmtMonth(e.month)}</span>
              <span>{fmtINR(e.amount)}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid var(--line)", margin: "10px 0" }} />
          <div className="card-row">
            <b>{t("tenantPayments.certificateTotal")}</b>
            <b>{fmtINR(data.totalPaid)}</b>
          </div>
          <button className="btn-solid no-print" style={{ marginTop: 16 }} onClick={() => window.print()}>
            {t("tenantPayments.certificatePrintBtn")}
          </button>
        </div>
      )}
    </>
  );
}

export function TenantPayments() {
  const { t } = useLanguage();
  const [ledgers, setLedgers] = useState<any[] | null>(null);
  const [past, setPast] = useState<any[]>([]);
  const [tenancies, setTenancies] = useState<any[]>([]);
  const [viewingReceipt, setViewingReceipt] = useState<{ tenancyId: string; entry: any; justPaid?: boolean } | null>(null);
  const [viewingCertificateFor, setViewingCertificateFor] = useState<string | null>(null);
  const [paying, setPaying] = useState<string | null>(null);
  const [showPayFor, setShowPayFor] = useState<string | null>(null);
  const toast = useToast();

  async function load() {
    const [{ ledgers, past }, { tenancies }] = await Promise.all([
      api.get("/api/ledger/mine"),
      api.get("/api/tenancies/mine"),
    ]);
    setLedgers(ledgers);
    // The Home card's Pay button lands here with the pay options already open.
    try {
      if (sessionStorage.getItem("toletbook_open_pay")) {
        sessionStorage.removeItem("toletbook_open_pay");
        const due = ledgers.find((l: any) => l.entries[0]?.status === "pending");
        if (due) setShowPayFor(due.entries[0].id);
      }
    } catch {}
    setPast(past ?? []);
    setTenancies(tenancies);
  }

  useEffect(() => {
    load();
  }, []);

  // Rent always pays the owner directly (UPI deep link or bank transfer) —
  // never routed through ToLetBook's own account. The tenant self-attests
  // once they've actually paid via the same PATCH /api/ledger/:id endpoint
  // this app has always used for that, but the server now lands a tenant's
  // own claim in 'pending_confirmation' rather than 'paid' — the owner
  // still has to confirm the money actually arrived before a receipt
  // exists, so there's nothing to show yet beyond that pending state.
  async function markPaid(
    entryId: string,
    tenancyId: string,
    mode: "upi" | "bank" | "cash",
    extra: { reference?: string; noReference?: boolean; note?: string } = {}
  ) {
    setPaying(entryId);
    try {
      await api.patch(`/api/ledger/${entryId}`, { status: "paid", mode, ...extra });
      toast(t("tenantPayments.paymentPendingConfirmationToast"));
      load();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("tenantPayments.paymentFailedToast"));
    } finally {
      setPaying(null);
    }
  }

  function upiIntentLink(vpa: string, payeeName: string, amount: number, note: string) {
    const params = new URLSearchParams({ pa: vpa, pn: payeeName, am: String(amount), cu: "INR", tn: note });
    return `upi://pay?${params.toString()}`;
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(t("tenantPayments.upiIdCopiedToast"));
    } catch {
      // clipboard access denied — nothing more we can do here
    }
  }

  function confirmMarkPaid(
    entryId: string,
    tenancyId: string,
    mode: "upi" | "bank" | "cash",
    extra: { reference?: string; noReference?: boolean; note?: string } = {}
  ) {
    if (!confirm(t(mode === "cash" ? "tenantPayments.markPaidCashConfirm" : "tenantPayments.markPaidUpiConfirm"))) return;
    setShowPayFor(null);
    markPaid(entryId, tenancyId, mode, extra);
  }

  if (!ledgers) return <div className="spinner" />;
  if (ledgers.length === 0 && past.length === 0) return <div className="empty-state">{t("tenantPayments.noActiveTenancy")}</div>;

  if (viewingCertificateFor) {
    return <CertificateView tenancyId={viewingCertificateFor} onBack={() => setViewingCertificateFor(null)} />;
  }

  if (viewingReceipt) {
    const t = tenancies.find((x) => x.id === viewingReceipt.tenancyId);
    // Falls back to the past tenancy's own embedded property/owner fields —
    // it won't show up in `tenancies` (that endpoint is active-only), so
    // this is the only place a receipt for an ended tenancy can get them.
    const p = past.find((x) => x.tenancyId === viewingReceipt.tenancyId);
    return (
      <RentReceipt
        entry={viewingReceipt.entry}
        tenantName={t?.name ?? "—"}
        propertyLabel={t?.property?.label ?? p?.propertyLabel ?? "—"}
        propertyLocality={t?.property?.locality ?? p?.propertyLocality}
        ownerName={t?.property?.ownerName ?? p?.ownerName}
        justPaid={viewingReceipt.justPaid}
        onBack={() => setViewingReceipt(null)}
      />
    );
  }

  return (
    <>
      <div className="section-title">{t("tenantPayments.title")}</div>
      {ledgers.map((l) => {
        const latest = l.entries[0];
        const tenancy = tenancies.find((x) => x.id === l.tenancyId);
        const ownerUpiId = tenancy?.property?.ownerUpiId as string | null | undefined;
        const ownerName = tenancy?.property?.ownerName as string | null | undefined;
        const ownerBankHolder = tenancy?.property?.ownerBankAccountHolder as string | null | undefined;
        const ownerBankNumber = tenancy?.property?.ownerBankAccountNumber as string | null | undefined;
        const ownerBankIfsc = tenancy?.property?.ownerBankIfsc as string | null | undefined;
        const hasBank = !!(ownerBankHolder && ownerBankNumber && ownerBankIfsc);
        const payMode: "upi" | "bank" | null = ownerUpiId ? "upi" : hasBank ? "bank" : null;
        return (
          <div key={l.tenancyId}>
            <div className="card">
              <div className="card-row">
                <div>
                  <b>{fmtMonth(latest?.month)}</b>
                  <div className="sub">
                    {latest?.status === "paid"
                      ? t("tenantPayments.paidStatus")
                      : latest?.status === "pending_confirmation"
                        ? t("tenantPayments.pendingConfirmationStatus")
                        : t("tenantPayments.rentDue")}
                  </div>
                </div>
                <span className={`badge ${latest?.status}`}>{fmtINR(latest?.amount ?? 0)}</span>
              </div>
              {latest && <ChargeLines rentAmount={latest.rentAmount} charges={latest.charges} total={latest.amount} />}
              {latest?.status === "pending" && latest.reminderSentAt && (
                <div
                  style={{
                    background: "var(--cash-tint)",
                    color: "var(--cash)",
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "8px 12px",
                    borderRadius: 11,
                    marginTop: 12,
                  }}
                >
                  {t("tenantPayments.reminderBanner")}
                </div>
              )}
              {latest?.status === "pending" && latest.rejectionReason && (
                <div
                  style={{
                    background: "var(--danger-tint, #fdecea)",
                    color: "var(--danger)",
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "10px 12px",
                    borderRadius: 11,
                    marginTop: 12,
                  }}
                >
                  {t("tenantPayments.rejectedBanner", { reason: latest.rejectionReason })}
                </div>
              )}
              {latest?.status === "pending" && (
                <>
                  <button
                    className="btn-primary"
                    style={{ marginTop: 12 }}
                    disabled={paying === latest.id}
                    onClick={() => setShowPayFor(showPayFor === latest.id ? null : latest.id)}
                  >
                    {paying === latest.id ? t("tenantPayments.payingBtn") : t("tenantPayments.payNow", { amount: fmtINR(latest.amount) })}
                  </button>
                  {showPayFor === latest.id && (
                    <PayPanel
                      amount={latest.amount}
                      month={latest.month}
                      ownerUpiId={ownerUpiId ?? null}
                      ownerName={ownerName ?? null}
                      bank={hasBank ? { holder: ownerBankHolder!, number: ownerBankNumber!, ifsc: ownerBankIfsc! } : null}
                      busy={paying === latest.id}
                      onCopy={copyText}
                      upiLink={upiIntentLink}
                      onClaim={(mode, extra) => confirmMarkPaid(latest.id, l.tenancyId, mode, extra)}
                    />
                  )}
                </>
              )}
              {latest?.status === "paid" && (
                <button
                  className="btn-outline btn-sm"
                  style={{ marginTop: 12 }}
                  onClick={() => setViewingReceipt({ tenancyId: l.tenancyId, entry: latest })}
                >
                  {t("tenantPayments.viewReceiptBtn")}
                </button>
              )}
              {latest?.status === "pending_confirmation" && (
                <div
                  style={{
                    background: "var(--cash-tint)",
                    color: "var(--cash)",
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "10px 12px",
                    borderRadius: 11,
                    marginTop: 12,
                  }}
                >
                  {latest.mode === "cash"
                    ? t("tenantPayments.pendingConfirmationCashNote")
                    : t("tenantPayments.pendingConfirmationNote", { mode: latest.mode?.toUpperCase() ?? "UPI" })}
                  {latest.paymentReference && <div style={{ marginTop: 4 }}>{t("tenantPayments.referenceLine", { ref: latest.paymentReference })}</div>}
                </div>
              )}
            </div>

            <button
              type="button"
              className="link-btn"
              style={{ marginTop: 10 }}
              onClick={() => setViewingCertificateFor(l.tenancyId)}
            >
              {t("tenantPayments.getCertificateBtn")}
            </button>

            {l.entries.length > 1 && (
              <>
                <div className="section-title" style={{ margin: "16px 0 8px", fontSize: 12 }}>
                  {t("tenantPayments.history")}
                </div>
                {l.entries.slice(1).map((e: any) => (
                  <div
                    className="card"
                    key={e.id}
                    onClick={() => e.status === "paid" && setViewingReceipt({ tenancyId: l.tenancyId, entry: e })}
                    style={{ cursor: e.status === "paid" ? "pointer" : "default" }}
                  >
                    <div className="card-row">
                      <span className="sub">{fmtMonth(e.month)}</span>
                      <span className={`badge ${e.status}`}>{fmtINR(e.amount)}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })}

      {past.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: ledgers.length > 0 ? 20 : 0 }}>
            {t("tenantPayments.pastRentalsHeading")}
          </div>
          {past.map((p) => (
            <div key={p.tenancyId} style={{ marginBottom: 16 }}>
              <div className="card">
                <div className="card-row">
                  <div>
                    <b>{p.propertyLabel ?? t("tenantPayments.pastRentalUnknownProperty")}</b>
                    {p.propertyLocality && <div className="sub">{p.propertyLocality}</div>}
                  </div>
                </div>
                {(p.startDate || p.exitDate) && (
                  <div className="sub" style={{ marginTop: 6 }}>
                    {t("tenantPayments.pastRentalDateRange", {
                      start: p.startDate ? new Date(p.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—",
                      end: p.exitDate ? new Date(p.exitDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—",
                    })}
                  </div>
                )}
                <button
                  type="button"
                  className="link-btn"
                  style={{ marginTop: 10 }}
                  onClick={() => setViewingCertificateFor(p.tenancyId)}
                >
                  {t("tenantPayments.getCertificateBtn")}
                </button>
              </div>
              {p.entries.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {p.entries.map((e: any) => (
                    <div
                      className="card"
                      key={e.id}
                      onClick={() => e.status === "paid" && setViewingReceipt({ tenancyId: p.tenancyId, entry: e })}
                      style={{ cursor: e.status === "paid" ? "pointer" : "default", marginTop: 6 }}
                    >
                      <div className="card-row">
                        <span className="sub">{fmtMonth(e.month)}</span>
                        <span className={`badge ${e.status}`}>{fmtINR(e.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
}

type ClaimExtra = { reference?: string; noReference?: boolean; note?: string };

// What the tenant sees after tapping Pay: pick how they paid (UPI, bank
// transfer, or cash), do it, and tell us. Nothing here moves money through
// ToLetBook — the owner confirms the claim on their side.
function PayPanel({
  amount,
  month,
  ownerUpiId,
  ownerName,
  bank,
  busy,
  onCopy,
  upiLink,
  onClaim,
}: {
  amount: number;
  month: string;
  ownerUpiId: string | null;
  ownerName: string | null;
  bank: { holder: string; number: string; ifsc: string } | null;
  busy: boolean;
  onCopy: (text: string) => void;
  upiLink: (vpa: string, payeeName: string, amount: number, note: string) => string;
  onClaim: (mode: "upi" | "bank" | "cash", extra: ClaimExtra) => void;
}) {
  const { t } = useLanguage();
  const [method, setMethod] = useState<"upi" | "bank" | "cash">(ownerUpiId ? "upi" : bank ? "bank" : "cash");
  const [reference, setReference] = useState("");
  const [noRef, setNoRef] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  function pick(m: "upi" | "bank" | "cash") {
    setMethod(m);
    setError("");
  }

  function submit() {
    if (method === "upi") {
      if (!noRef && !/^\d{12}$/.test(reference)) return setError(t("tenantPayments.upiRefInvalid"));
      onClaim("upi", noRef ? { noReference: true } : { reference });
    } else if (method === "bank") {
      onClaim("bank", reference.trim() ? { reference: reference.trim() } : {});
    } else {
      onClaim("cash", note.trim() ? { note: note.trim() } : {});
    }
  }

  const box: React.CSSProperties = { marginTop: 8, padding: 12, borderRadius: 12, background: "var(--surface)", border: "1px solid var(--line)" };
  const methods: ("upi" | "bank" | "cash")[] = [...(ownerUpiId ? (["upi"] as const) : []), ...(bank ? (["bank"] as const) : []), "cash"];

  return (
    <div style={box}>
      <div className="sub" style={{ marginBottom: 8 }}>{t("tenantPayments.chooseHowPaid")}</div>
      <div className="chip-row" style={{ marginBottom: 12 }}>
        {methods.map((m) => (
          <div key={m} className={`chip${method === m ? " selected" : ""}`} onClick={() => pick(m)}>
            {t(m === "upi" ? "tenantPayments.methodUpi" : m === "bank" ? "tenantPayments.methodBank" : "tenantPayments.methodCash")}
          </div>
        ))}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {method === "upi" && ownerUpiId && (
        <>
          <div className="sub">{t("tenantPayments.ownerUpiIdLabel")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <b style={{ fontSize: 15 }}>{ownerUpiId}</b>
            <button type="button" className="link-btn" onClick={() => onCopy(ownerUpiId)}>
              {t("tenantPayments.copyUpiBtn")}
            </button>
          </div>
          <a
            href={upiLink(ownerUpiId, ownerName ?? "ToLetBook Owner", amount, `${t("tenantPayments.rentDue")} ${month}`)}
            className="btn-outline btn-sm"
            style={{ marginTop: 10, display: "block", textAlign: "center" }}
          >
            {t("tenantPayments.openUpiAppBtn")}
          </a>

          {!noRef && (
            <div className="field" style={{ marginTop: 14 }}>
              <label>{t("tenantPayments.upiRefLabel")}</label>
              <div className="input-wrap">
                <input
                  inputMode="numeric"
                  maxLength={12}
                  value={reference}
                  onChange={(e) => setReference(e.target.value.replace(/\D/g, "").slice(0, 12))}
                  placeholder={t("tenantPayments.upiRefPlaceholder")}
                />
              </div>
              <div className="field-hint">{t("tenantPayments.upiRefHint")}</div>
            </div>
          )}
          <button type="button" className="link-btn" style={{ marginTop: 6, display: "block" }} onClick={() => setNoRef((v) => !v)}>
            {noRef ? t("tenantPayments.haveRefLink") : t("tenantPayments.noRefLink")}
          </button>
        </>
      )}

      {method === "bank" && bank && (
        <>
          <div className="sub">{t("tenantPayments.ownerBankDetailsLabel")}</div>
          <div style={{ marginTop: 6, fontSize: 14 }}>
            <div>{bank.holder}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
              <b>{bank.number}</b>
              <button type="button" className="link-btn" onClick={() => onCopy(bank.number)}>
                {t("tenantPayments.copyUpiBtn")}
              </button>
            </div>
            <div style={{ marginTop: 2 }}>{bank.ifsc}</div>
          </div>
          <div className="field" style={{ marginTop: 14 }}>
            <label>{t("tenantPayments.bankRefLabel")}</label>
            <div className="input-wrap">
              <input value={reference} maxLength={22} onChange={(e) => setReference(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} />
            </div>
          </div>
        </>
      )}

      {method === "cash" && (
        <>
          <div className="sub">{t("tenantPayments.cashInfo")}</div>
          <div className="field" style={{ marginTop: 12 }}>
            <label>{t("tenantPayments.cashNoteLabel")}</label>
            <div className="input-wrap">
              <input value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} placeholder={t("tenantPayments.cashNotePlaceholder")} />
            </div>
          </div>
        </>
      )}

      <button type="button" className="btn-solid btn-sm" style={{ marginTop: 12, display: "block", width: "100%" }} disabled={busy} onClick={submit}>
        {method === "cash" ? t("tenantPayments.markPaidCashBtn", { amount: fmtINR(amount) }) : t("tenantPayments.markPaidUpiBtn")}
      </button>
    </div>
  );
}
